import React, { useState, useEffect } from 'react';
import { Camera, Calendar, Clock, Plus, Bell, ChevronLeft, X, MapPin, AlertCircle, List, Filter, LogOut, Edit, FileText, Trash2, Image as ImageIcon } from 'lucide-react';
import { db, storage } from './firebase';
import { collection, addDoc, onSnapshot, deleteDoc, doc, query, orderBy, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

function App() {
  const [isAuth, setIsAuth] = useState(false);
  const [pin, setPin] = useState('');
  const [pinErr, setPinErr] = useState(false);
  const [view, setView] = useState('home');
  const [events, setEvents] = useState([]);
  const [title, setTitle] = useState('');
  const [loc, setLoc] = useState('');
  const [notes, setNotes] = useState('');
  const [reminder, setReminder] = useState('1day');
  const [cat, setCat] = useState('other');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [recur, setRecur] = useState(false);
  const [now, setNow] = useState(new Date());
  const [delConfirm, setDelConfirm] = useState(null);
  const [editConfirm, setEditConfirm] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showFilter, setShowFilter] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [userNotes, setUserNotes] = useState([]);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteText, setNoteText] = useState('');
  const [selectedNote, setSelectedNote] = useState(null);
  const [editingNote, setEditingNote] = useState(false);
  const [noteDelConfirm, setNoteDelConfirm] = useState(null);
  const [viewImage, setViewImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [deleteImageConfirm, setDeleteImageConfirm] = useState(null);

  useEffect(() => {
    const auth = localStorage.getItem('huskdet_auth');
    if (auth === '1') setIsAuth(true);
    
    const q = query(collection(db, 'events'), orderBy('startTime', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const eventsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEvents(eventsData);
    });

    const notesQuery = query(collection(db, 'notes'), orderBy('createdAt', 'desc'));
    const unsubscribeNotes = onSnapshot(notesQuery, (snapshot) => {
      const notesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUserNotes(notesData);
    });
    
    const today = new Date();
    setDate(today.toISOString().split('T')[0]);
    setTime('09:00');
    
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => {
      clearInterval(timer);
      unsubscribe();
      unsubscribeNotes();
    };
  }, []);

  const login = () => {
    if (pin === '6364') {
      setIsAuth(true);
      localStorage.setItem('huskdet_auth', '1');
      setPinErr(false);
      setPin('');
    } else {
      setPinErr(true);
      setPin('');
      setTimeout(() => setPinErr(false), 2000);
    }
  };

  const logout = () => {
    setIsAuth(false);
    localStorage.removeItem('huskdet_auth');
    setView('home');
  };

  const cats = {
    doctor: { name: 'Legetime', icon: '🏥', label: 'LEGE', color: 'red' },
    homevisit: { name: 'Behandlingstime', icon: '🏃‍♀️', label: 'Behandlingstime ', color: 'purple' },
    other: { name: 'Annet', icon: '📅', label: 'AVTALE', color: 'orange' }
  };

  const handleImageUpload = async (eventId, file) => {
    if (!file) return;
    
    setUploadingImage(eventId);
    try {
      const imageRef = ref(storage, `events/${eventId}/${Date.now()}_${file.name}`);
      await uploadBytes(imageRef, file);
      const imageUrl = await getDownloadURL(imageRef);
      
      await updateDoc(doc(db, 'events', eventId), {
        imageUrl: imageUrl
      });
      
      setUploadingImage(null);
    } catch (error) {
      console.error('Feil ved opplasting av bilde:', error);
      alert('Kunne ikke laste opp bilde. Prøv igjen.');
      setUploadingImage(null);
    }
  };

  const confirmDeleteImage = async () => {
    const event = deleteImageConfirm;
    if (!event || !event.imageUrl) {
      setDeleteImageConfirm(null);
      return;
    }
    
    try {
      const imageRef = ref(storage, event.imageUrl);
      await deleteObject(imageRef);
      
      await updateDoc(doc(db, 'events', event.id), {
        imageUrl: null
      });
      
      setDeleteImageConfirm(null);
    } catch (error) {
      console.error('Feil ved sletting av bilde:', error);
      alert('Kunne ikke slette bilde. Prøv igjen.');
      setDeleteImageConfirm(null);
    }
  };

  const addEv = async () => {
    if (!title.trim() || !date || !time) return;
    const [y, m, d] = date.split('-').map(Number);
    const [h, min] = time.split(':').map(Number);
    const start = new Date(y, m - 1, d, h, min);
    const end = new Date(start.getTime() + 3600000);

    const ev = {
      title,
      startTime: start.toISOString(),
      endTime: end.toISOString(),
      location: loc,
      notes: notes,
      reminder: reminder,
      category: cat,
      recurring: recur ? 'weekly' : null,
      createdBy: 'Bruker',
      createdAt: new Date().toISOString(),
      reminder4DaysSent: false,
      reminder1DaySent: false,
      reminderSent: false,
      imageUrl: null
    };

    try {
      await addDoc(collection(db, 'events'), ev);
      setTitle('');
      setLoc('');
      setNotes('');
      setReminder('1day');
      setCat('other');
      setRecur(false);
      setDate(new Date().toISOString().split('T')[0]);
      setTime('09:00');
    } catch (error) {
      console.error('Error adding event:', error);
      alert('Kunne ikke legge til avtale. Sjekk internett-tilkoblingen.');
    }
  };

  const startEdit = (event) => {
    setEditConfirm(event);
  };

  const confirmEdit = () => {
    const event = editConfirm;
    setEditingEvent(event);
    setTitle(event.title);
    setLoc(event.location || '');
    setNotes(event.notes || '');
    setReminder(event.reminder || '1day');
    setCat(event.category);
    setRecur(!!event.recurring);
    
    const eventDate = new Date(event.startTime);
    setDate(eventDate.toISOString().split('T')[0]);
    setTime(eventDate.toTimeString().slice(0, 5));
    
    setEditConfirm(null);
    setView('add');
  };

  const updateEv = async () => {
    if (!editingEvent || !title.trim() || !date || !time) return;
    
    const [y, m, d] = date.split('-').map(Number);
    const [h, min] = time.split(':').map(Number);
    const start = new Date(y, m - 1, d, h, min);
    const end = new Date(start.getTime() + 3600000);

    try {
      await updateDoc(doc(db, 'events', editingEvent.id), {
        title,
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        location: loc,
        notes: notes,
        reminder: reminder,
        category: cat,
        recurring: recur ? 'weekly' : null
      });
      
      setEditingEvent(null);
      setTitle('');
      setLoc('');
      setNotes('');
      setReminder('1day');
      setCat('other');
      setRecur(false);
      setDate(new Date().toISOString().split('T')[0]);
      setTime('09:00');
      setView('calendar');
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Kunne ikke oppdatere avtale. Sjekk internett-tilkoblingen.');
    }
  };

  const cancelEdit = () => {
    setEditingEvent(null);
    setTitle('');
    setLoc('');
    setNotes('');
    setReminder('1day');
    setCat('other');
    setRecur(false);
    setDate(new Date().toISOString().split('T')[0]);
    setTime('09:00');
    setView('calendar');
  };

  const reqDel = (e) => setDelConfirm(e);

  const confDel = async () => {
    if (delConfirm) {
      try {
        if (delConfirm.imageUrl) {
          const imageRef = ref(storage, delConfirm.imageUrl);
          await deleteObject(imageRef).catch(() => {});
        }
        await deleteDoc(doc(db, 'events', delConfirm.id));
        setDelConfirm(null);
      } catch (error) {
        console.error('Error deleting event:', error);
        alert('Kunne ikke slette avtale. Sjekk internett-tilkoblingen.');
      }
    }
  };

  const addNote = async () => {
    if (!noteTitle.trim()) return;
    const note = {
      title: noteTitle,
      text: noteText,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    try {
      await addDoc(collection(db, 'notes'), note);
      setNoteTitle('');
      setNoteText('');
      setView('notes');
    } catch (error) {
      console.error('Feil ved lagring av notat:', error);
      alert('Kunne ikke lagre notat. Sjekk internett-tilkoblingen.');
    }
  };

  const updateNote = async () => {
    if (!selectedNote || !noteTitle.trim()) return;
    try {
      await updateDoc(doc(db, 'notes', selectedNote.id), {
        title: noteTitle,
        text: noteText,
        updatedAt: new Date().toISOString()
      });
      setSelectedNote({...selectedNote, title: noteTitle, text: noteText, updatedAt: new Date().toISOString()});
      setEditingNote(false);
    } catch (error) {
      console.error('Feil ved oppdatering av notat:', error);
      alert('Kunne ikke oppdatere notat. Sjekk internett-tilkoblingen.');
    }
  };

  const deleteNote = async (id) => {
    try {
      await deleteDoc(doc(db, 'notes', id));
      setNoteDelConfirm(null);
      setSelectedNote(null);
      setView('notes');
    } catch (error) {
      console.error('Feil ved sletting av notat:', error);
      alert('Kunne ikke slette notat. Sjekk internett-tilkoblingen.');
    }
  };

  const openNote = (note) => {
    setSelectedNote(note);
    setNoteTitle(note.title);
    setNoteText(note.text || '');
    setEditingNote(false);
    setView('noteDetail');
  };

  const sameDay = (d1, d2) => {
    const a = new Date(d1);
    const b = new Date(d2);
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  };

  const fmtDate = (d) => new Date(d).toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'long' });
  const fmtTime = (d) => new Date(d).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
  const fmtDateShort = (d) => new Date(d).toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const timeUntil = (d) => {
    const diff = new Date(d) - now;
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    if (days > 0) return `om ${days} dag${days > 1 ? 'er' : ''}`;
    if (hours > 0) return `om ${hours} time${hours > 1 ? 'r' : ''}`;
    return 'snart';
  };

  const getReminderText = (r) => {
    if (r === '2hours') return 'Varsel 2 timer før';
    if (r === '4hours') return 'Varsel 4 timer før';
    if (r === '1day') return 'Varsel 1 dag før';
    if (r === '4days+1day') return 'Varsel 4 dager før + 1 dag før';
    return 'Ingen påminnelse';
  };

  const filtered = (evs) => {
    if (filter === 'all') return evs;
    return evs.filter(e => e.category === filter);
  };

  const renderEventCard = (e, isToday = false) => {
    const cardClasses = isToday 
      ? "rounded-2xl shadow-xl overflow-hidden border-4 border-white"
      : `rounded-xl p-4 bg-white shadow-xl border-l-4 ${e.category === 'doctor' ? 'border-red-500' : e.category === 'homevisit' ? 'border-purple-500' : 'border-orange-500'}`;
    
    const bgClass = e.category === 'doctor' 
      ? 'bg-gradient-to-br from-red-400 to-red-600' 
      : e.category === 'homevisit' 
      ? 'bg-gradient-to-br from-purple-400 to-purple-600' 
      : 'bg-gradient-to-br from-orange-400 to-orange-600';

    return (
      <div key={e.id} className={cardClasses}>
        {isToday ? (
          <div className={`p-4 text-white ${bgClass}`}>
            <div className="flex justify-between gap-3 mb-2">
              <div className="flex-1">
                <div className="bg-white bg-opacity-30 inline-block px-2 py-1 rounded-lg mb-2">
                  <span className="font-bold text-sm">{cats[e.category]?.icon} {cats[e.category]?.label}</span>
                </div>
                <h3 className="text-xl font-bold leading-tight">{e.title}</h3>
              </div>
              <div className="flex gap-2">
                <button onClick={() => startEdit(e)} className="bg-white bg-opacity-30 p-2 rounded-lg hover:bg-opacity-50 transition h-fit">
                  <Edit size={18} />
                </button>
                <button onClick={() => reqDel(e)} className="bg-white bg-opacity-30 p-2 rounded-lg hover:bg-opacity-50 transition h-fit">
                  <X size={18} strokeWidth={3} />
                </button>
              </div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-3 space-y-2 text-base">
              <p className="font-bold flex items-center gap-2"><Clock size={16} />Klokken {fmtTime(e.startTime)}</p>
              {e.location && <p className="font-bold flex items-center gap-2"><MapPin size={16} />{e.location}</p>}
              {e.notes && <p className="font-semibold flex items-start gap-2 text-sm"><span className="text-lg flex-shrink-0">📋</span><span>{e.notes}</span></p>}
              {e.reminder && e.reminder !== 'none' && <p className="font-bold flex items-center gap-2 text-sm"><Bell size={14} />{getReminderText(e.reminder)}</p>}
              {e.recurring && <p className="font-bold text-sm">🔄 Gjentas hver uke</p>}
              <div className="flex gap-2 pt-2">
                {e.imageUrl ? (
                  <>
                    <button onClick={() => setViewImage(e.imageUrl)} className="bg-white bg-opacity-30 px-3 py-2 rounded-lg hover:bg-opacity-50 transition flex items-center gap-2 text-sm font-bold">
                      <ImageIcon size={16} />
                      Se bilde
                    </button>
                    <button onClick={() => setDeleteImageConfirm(e)} className="bg-red-500 bg-opacity-80 px-3 py-2 rounded-lg hover:bg-opacity-100 transition flex items-center gap-2 text-sm font-bold">
                      <Trash2 size={16} />
                      Slett bilde
                    </button>
                  </>
                ) : (
                  <label className={`bg-white bg-opacity-30 px-3 py-2 rounded-lg hover:bg-opacity-50 transition flex items-center gap-2 text-sm font-bold cursor-pointer ${uploadingImage === e.id ? 'opacity-50' : ''}`}>
                    <Camera size={16} />
                    {uploadingImage === e.id ? 'Laster opp...' : 'Legg til bilde'}
                    <input type="file" accept="image/*" onChange={(ev) => handleImageUpload(e.id, ev.target.files[0])} className="hidden" disabled={uploadingImage === e.id} />
                  </label>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-between gap-3">
            <div className="flex-1">
              <div className={`inline-block px-2 py-1 rounded-lg mb-2 font-bold text-xs ${e.category === 'doctor' ? 'bg-red-100 text-red-800' : e.category === 'homevisit' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'}`}>
                {cats[e.category]?.icon} {cats[e.category]?.label} • {timeUntil(e.startTime)}
              </div>
              <h3 className="text-base font-bold text-gray-800 mb-2">{e.title}</h3>
              <div className={`p-2 rounded-lg text-sm ${e.category === 'doctor' ? 'bg-red-50' : e.category === 'homevisit' ? 'bg-purple-50' : 'bg-orange-50'}`}>
                <div className="flex items-center gap-2 mb-1"><Calendar size={14} /><span className="font-bold">{fmtDate(e.startTime)}</span></div>
                <div className="flex items-center gap-2 mb-1"><Clock size={14} /><span className="font-bold">Kl. {fmtTime(e.startTime)}</span></div>
                {e.location && <div className="flex items-center gap-2 mb-1"><MapPin size={14} /><span className="font-bold">{e.location}</span></div>}
                {e.notes && <div className="flex items-start gap-2 mt-2 pt-2 border-t border-gray-200 text-xs"><span className="flex-shrink-0">📋</span><span>{e.notes}</span></div>}
                {e.reminder && e.reminder !== 'none' && <div className="flex items-center gap-1 mt-1 text-xs"><Bell size={12} /><span className="font-semibold">{getReminderText(e.reminder)}</span></div>}
                {e.recurring && <div className="text-xs font-semibold mt-1">🔄 Gjentas hver uke</div>}
                <div className="flex gap-2 mt-2 pt-2 border-t border-gray-200">
                  {e.imageUrl ? (
                    <>
                      <button onClick={() => setViewImage(e.imageUrl)} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-bold hover:bg-blue-200 transition flex items-center gap-1">
                        <ImageIcon size={12} />
                        Se bilde
                      </button>
                      <button onClick={() => setDeleteImageConfirm(e)} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded font-bold hover:bg-red-200 transition flex items-center gap-1">
                        <Trash2 size={12} />
                        Slett
                      </button>
                    </>
                  ) : (
                    <label className={`text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded font-bold hover:bg-gray-200 transition flex items-center gap-1 cursor-pointer ${uploadingImage === e.id ? 'opacity-50' : ''}`}>
                      <Camera size={12} />
                      {uploadingImage === e.id ? 'Laster...' : 'Legg til'}
                      <input type="file" accept="image/*" onChange={(ev) => handleImageUpload(e.id, ev.target.files[0])} className="hidden" disabled={uploadingImage === e.id} />
                    </label>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={() => startEdit(e)} className="bg-blue-100 text-blue-600 p-2 rounded-lg hover:bg-blue-200 transition"><Edit size={18} /></button>
              <button onClick={() => reqDel(e)} className="bg-red-100 text-red-600 p-2 rounded-lg hover:bg-red-200 transition"><X size={18} strokeWidth={3} /></button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!isAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="bg-blue-500 w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Calendar size={40} className="text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">HuskDet!</h1>
            <p className="text-lg text-gray-600">Av Aliasgar Amirali</p>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-xl font-bold text-gray-700 mb-3">🔒 PIN-kode</label>
              <input
                type="password"
                inputMode="numeric"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && login()}
                className="w-full px-5 py-4 text-2xl border-4 border-gray-300 rounded-2xl focus:border-blue-500 focus:outline-none text-center tracking-widest"
                placeholder="••••"
                maxLength="4"
              />
            </div>
            {pinErr && (
              <div className="bg-red-100 border-4 border-red-500 rounded-2xl p-4 text-center">
                <p className="text-red-700 font-bold text-lg">❌ Feil PIN-kode</p>
              </div>
            )}
            <button
              onClick={login}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-5 px-6 rounded-2xl text-xl font-bold hover:from-blue-600 hover:to-purple-600 transition shadow-xl"
            >
              Logg inn
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'home') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-xl w-full">
          <div className="text-center mb-8">
            <div className="bg-gradient-to-br from-blue-500 to-purple-500 w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center shadow-2xl">
              <Calendar size={48} className="text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-3">HuskDet!</h1>
            <p className="text-lg text-gray-600 mb-1">{now.toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
            <p className="text-3xl font-bold text-gray-800">{now.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          <div className="space-y-3">
            <button onClick={() => setView('add')} className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-5 px-6 rounded-2xl text-2xl font-bold hover:from-blue-600 hover:to-blue-700 transition shadow-xl flex items-center justify-center gap-3">
              <Plus size={28} />
              Legg til avtale
            </button>
            <button onClick={() => setView('calendar')} className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white py-5 px-6 rounded-2xl text-2xl font-bold hover:from-purple-600 hover:to-purple-700 transition shadow-xl flex items-center justify-center gap-3">
              <List size={28} />
              Se kalender
            </button>
            <button onClick={() => setView('notes')} className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-5 px-6 rounded-2xl text-2xl font-bold hover:from-yellow-600 hover:to-orange-600 transition shadow-xl flex items-center justify-center gap-3">
              <FileText size={28} />
              Notater
            </button>
            <button onClick={logout} className="w-full bg-gray-300 text-gray-700 py-4 px-6 rounded-2xl text-lg font-bold hover:bg-gray-400 transition flex items-center justify-center gap-2">
              <LogOut size={22} />
              Logg ut
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'notes') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="bg-white shadow-lg">
          <div className="max-w-4xl mx-auto p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setView('home')} className="p-2 hover:bg-gray-100 rounded-xl transition">
                  <ChevronLeft size={28} />
                </button>
                <h1 className="text-xl font-bold text-gray-800">📝 Notater</h1>
              </div>
              <div className="text-lg font-bold text-gray-800">{now.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}</div>
            </div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto p-4">
          <button onClick={() => { setNoteTitle(''); setNoteText(''); setView('addNote'); }} className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-4 px-6 rounded-2xl text-xl font-bold hover:from-yellow-600 hover:to-orange-600 transition shadow-xl flex items-center justify-center gap-3 mb-6">
            <Plus size={24} />
            Legg til notat
          </button>
          <div className="space-y-3">
            {userNotes.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <FileText size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 text-lg font-semibold">Ingen notater ennå</p>
                <p className="text-gray-400 mt-2">Trykk på knappen over for å lage et notat</p>
              </div>
            ) : (
              userNotes.map(note => (
                <button key={note.id} onClick={() => openNote(note)} className="w-full bg-white rounded-2xl shadow-lg p-4 text-left hover:shadow-xl transition border-l-4 border-yellow-500">
                  <h3 className="text-lg font-bold text-gray-800 truncate">{note.title}</h3>
                  <p className="text-gray-500 text-sm mt-1">{fmtDateShort(note.createdAt)}</p>
                  {note.text && <p className="text-gray-600 mt-2 text-sm line-clamp-2">{note.text}</p>}
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  if (view === 'addNote') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="bg-white shadow-lg">
          <div className="max-w-4xl mx-auto p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setView('notes')} className="p-2 hover:bg-gray-100 rounded-xl transition">
                  <ChevronLeft size={28} />
                </button>
                <h1 className="text-xl font-bold text-gray-800">📝 Nytt notat</h1>
              </div>
              <div className="text-lg font-bold text-gray-800">{now.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}</div>
            </div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto p-4">
          <div className="bg-white rounded-3xl shadow-lg p-5 space-y-4">
            <div className="bg-yellow-50 border-4 border-yellow-300 rounded-2xl p-4">
              <label className="block text-lg font-bold text-yellow-900 mb-2">🏷️ Tittel</label>
              <input type="text" value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)} placeholder="F.eks. Lege sa..." className="w-full px-4 py-3 text-lg border-4 border-yellow-400 rounded-xl focus:border-yellow-600 focus:outline-none bg-white" />
            </div>
            <div className="bg-orange-50 border-4 border-orange-300 rounded-2xl p-4">
              <label className="block text-lg font-bold text-orange-900 mb-2">📋 Notat</label>
              <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Skriv notatet ditt her..." rows="8" className="w-full px-4 py-3 text-lg border-4 border-orange-400 rounded-xl focus:border-orange-600 focus:outline-none bg-white resize-none" />
            </div>
            <button onClick={addNote} disabled={!noteTitle.trim()} className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-4 px-6 rounded-2xl text-xl font-bold hover:from-yellow-600 hover:to-orange-600 disabled:from-gray-300 disabled:to-gray-400 transition shadow-lg">
              ✅ Lagre notat
            </button>
            <button onClick={() => setView('notes')} className="w-full bg-gray-200 text-gray-700 py-3 px-6 rounded-2xl text-base font-bold hover:bg-gray-300 transition">
              Avbryt
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'noteDetail' && selectedNote) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        {noteDelConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-sm w-full">
              <div className="text-center mb-6">
                <AlertCircle className="text-orange-500 mx-auto mb-4" size={60} />
                <h2 className="text-2xl font-bold text-gray-800 mb-4">⚠️ Slette notat?</h2>
                <div className="bg-gray-50 rounded-2xl p-4 mb-4">
                  <p className="text-xl font-bold text-gray-800">{noteDelConfirm.title}</p>
                </div>
                <p className="text-sm text-gray-600 mb-4">Dette kan ikke angres!</p>
              </div>
              <div className="space-y-3">
                <button onClick={() => deleteNote(noteDelConfirm.id)} className="w-full bg-red-500 text-white py-3 px-6 rounded-xl text-lg font-bold hover:bg-red-600 transition">🗑️ Ja, slett</button>
                <button onClick={() => setNoteDelConfirm(null)} className="w-full bg-gray-200 text-gray-700 py-3 px-6 rounded-xl text-lg font-bold hover:bg-gray-300 transition">Avbryt</button>
              </div>
            </div>
          </div>
        )}
        <div className="bg-white shadow-lg">
          <div className="max-w-4xl mx-auto p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => { setSelectedNote(null); setEditingNote(false); setView('notes'); }} className="p-2 hover:bg-gray-100 rounded-xl transition">
                  <ChevronLeft size={28} />
                </button>
                <h1 className="text-xl font-bold text-gray-800">📝 Notat</h1>
              </div>
              <div className="text-lg font-bold text-gray-800">{now.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}</div>
            </div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto p-4">
          <div className="bg-white rounded-3xl shadow-lg p-5">
            {editingNote ? (
              <div className="space-y-4">
                <div className="bg-yellow-50 border-4 border-yellow-300 rounded-2xl p-4">
                  <label className="block text-lg font-bold text-yellow-900 mb-2">🏷️ Tittel</label>
                  <input type="text" value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)} className="w-full px-4 py-3 text-lg border-4 border-yellow-400 rounded-xl focus:border-yellow-600 focus:outline-none bg-white" />
                </div>
                <div className="bg-orange-50 border-4 border-orange-300 rounded-2xl p-4">
                  <label className="block text-lg font-bold text-orange-900 mb-2">📋 Notat</label>
                  <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows="8" className="w-full px-4 py-3 text-lg border-4 border-orange-400 rounded-xl focus:border-orange-600 focus:outline-none bg-white resize-none" />
                </div>
                <div className="flex gap-3">
                  <button onClick={() => { setNoteTitle(selectedNote.title); setNoteText(selectedNote.text || ''); setEditingNote(false); }} className="flex-1 bg-gray-200 text-gray-700 py-3 px-6 rounded-xl text-lg font-bold hover:bg-gray-300 transition">Avbryt</button>
                  <button onClick={updateNote} className="flex-1 bg-green-500 text-white py-3 px-6 rounded-xl text-lg font-bold hover:bg-green-600 transition">✅ Lagre</button>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">{selectedNote.title}</h2>
                  <p className="text-gray-500 text-sm">Opprettet: {fmtDateShort(selectedNote.createdAt)}</p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-4 mb-6 min-h-[150px]">
                  {selectedNote.text ? <p className="text-gray-700 whitespace-pre-wrap text-lg">{selectedNote.text}</p> : <p className="text-gray-400 italic">Ingen notat-tekst</p>}
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setEditingNote(true)} className="flex-1 bg-blue-500 text-white py-3 px-6 rounded-xl text-lg font-bold hover:bg-blue-600 transition flex items-center justify-center gap-2">
                    <Edit size={20} />
                    Rediger
                  </button>
                  <button onClick={() => setNoteDelConfirm(selectedNote)} className="bg-red-100 text-red-600 py-3 px-6 rounded-xl text-lg font-bold hover:bg-red-200 transition flex items-center justify-center gap-2">
                    <Trash2 size={20} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (view === 'add') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="bg-white shadow-lg">
          <div className="max-w-4xl mx-auto p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={editingEvent ? cancelEdit : () => setView('home')} className="p-2 hover:bg-gray-100 rounded-xl transition">
                  <ChevronLeft size={28} />
                </button>
                <h1 className="text-xl font-bold text-gray-800">{editingEvent ? 'Rediger avtale' : 'Legg til avtale'}</h1>
              </div>
              <div className="text-lg font-bold text-gray-800">{now.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}</div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto p-4">
          <div className="bg-white rounded-3xl shadow-lg p-5 space-y-4">
            <div className="bg-indigo-50 border-4 border-indigo-300 rounded-2xl p-4">
              <label className="block text-lg font-bold text-indigo-900 mb-3">🏷️ Type avtale</label>
              <div className="space-y-2">
                <button onClick={() => setCat('doctor')} className={`w-full p-3 rounded-xl text-base font-bold border-4 transition ${cat === 'doctor' ? 'bg-red-500 text-white border-red-600 shadow-lg' : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'}`}>
                  🏥 Legetime 
                </button>
                <button onClick={() => setCat('homevisit')} className={`w-full p-3 rounded-xl text-base font-bold border-4 transition ${cat === 'homevisit' ? 'bg-purple-500 text-white border-purple-600 shadow-lg' : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'}`}>
                  🏃‍♀️ Behandlingstime 
                </button>
                <button onClick={() => setCat('other')} className={`w-full p-3 rounded-xl text-base font-bold border-4 transition ${cat === 'other' ? 'bg-orange-500 text-white border-orange-600 shadow-lg' : 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100'}`}>
                  📅 Annet
                </button>
              </div>
            </div>

            <div className="bg-blue-50 border-4 border-blue-300 rounded-2xl p-4">
              <label className="block text-lg font-bold text-blue-900 mb-2">📝 Hva skal du gjøre?</label>
              <textarea value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Skriv her..." rows="2" className="w-full px-4 py-3 text-lg border-4 border-blue-400 rounded-xl focus:border-blue-600 focus:outline-none bg-white resize-none" />
            </div>

            <div className="bg-green-50 border-4 border-green-300 rounded-2xl p-4">
              <label className="block text-lg font-bold text-green-900 mb-2">📍 Hvor?</label>
              <input type="text" value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="F.eks. Helsehuset..." className="w-full px-4 py-3 text-lg border-4 border-green-400 rounded-xl focus:border-green-600 focus:outline-none bg-white" />
            </div>

            <div className="bg-yellow-50 border-4 border-yellow-300 rounded-2xl p-4">
              <label className="block text-lg font-bold text-yellow-900 mb-2">📋 Notater</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="F.eks. Husk legitimasjon..." rows="2" className="w-full px-4 py-3 text-base border-4 border-yellow-400 rounded-xl focus:border-yellow-600 focus:outline-none bg-white resize-none" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-purple-50 border-4 border-purple-300 rounded-2xl p-4">
                <label className="block text-lg font-bold text-purple-900 mb-2">📅 Dato</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full px-3 py-3 text-base border-4 border-purple-400 rounded-xl focus:border-purple-600 focus:outline-none bg-white" />
              </div>
              <div className="bg-orange-50 border-4 border-orange-300 rounded-2xl p-4">
                <label className="block text-lg font-bold text-orange-900 mb-2">🕐 Tid</label>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-full px-3 py-3 text-base border-4 border-orange-400 rounded-xl focus:border-orange-600 focus:outline-none bg-white" />
              </div>
            </div>

            <div className="bg-teal-50 border-4 border-teal-300 rounded-2xl p-4">
              <label className="block text-lg font-bold text-teal-900 mb-2">🔔 Påminn meg</label>
              <select value={reminder} onChange={(e) => setReminder(e.target.value)} className="w-full px-4 py-3 text-base border-4 border-teal-400 rounded-xl focus:border-teal-600 focus:outline-none bg-white font-bold">
                <option value="2hours">2 timer før</option>
                <option value="4hours">4 timer før</option>
                <option value="1day">1 dag før (etter kl. 12:00)</option>
                <option value="4days+1day">4 dager før + 1 dag før</option>
                <option value="none">Ingen påminnelse</option>
              </select>
              <p className="text-xs text-teal-700 mt-2 font-semibold">💡 Tips: "4 dager før + 1 dag før" sender 2 SMS!</p>
            </div>

            {cat === 'homevisit' && (
              <div className="bg-pink-50 border-4 border-pink-300 rounded-2xl p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={recur} onChange={(e) => setRecur(e.target.checked)} className="w-6 h-6 rounded" />
                  <span className="text-lg font-bold text-pink-900">🔄 Kommer hver uke</span>
                </label>
              </div>
            )}

            <button onClick={editingEvent ? updateEv : () => { addEv(); setView('calendar'); }} disabled={!title.trim() || !date || !time} className="w-full bg-green-500 text-white py-4 px-6 rounded-2xl text-xl font-bold hover:bg-green-600 disabled:bg-gray-300 transition shadow-lg">
              {editingEvent ? '✅ Oppdater avtale' : '✅ Lagre avtale'}
            </button>

            <button onClick={editingEvent ? cancelEdit : () => setView('home')} className="w-full bg-gray-200 text-gray-700 py-3 px-6 rounded-2xl text-base font-bold hover:bg-gray-300 transition">
              Avbryt
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 pb-20">
      {deleteImageConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-sm w-full">
            <div className="text-center mb-6">
              <AlertCircle className="text-orange-500 mx-auto mb-4" size={60} />
              <h2 className="text-2xl font-bold text-gray-800 mb-4">⚠️ Slette bilde?</h2>
              <div className="bg-gray-50 rounded-2xl p-4 mb-4">
                <p className="text-xl font-bold text-gray-800 mb-2">{deleteImageConfirm.title}</p>
                <p className="text-base text-gray-600">{fmtDate(deleteImageConfirm.startTime)}</p>
              </div>
              <p className="text-sm text-gray-600 mb-4">Bildet slettes permanent!</p>
            </div>
            <div className="space-y-3">
              <button onClick={confirmDeleteImage} className="w-full bg-red-500 text-white py-3 px-6 rounded-xl text-lg font-bold hover:bg-red-600 transition">🗑️ Ja, slett bildet</button>
              <button onClick={() => setDeleteImageConfirm(null)} className="w-full bg-gray-200 text-gray-700 py-3 px-6 rounded-xl text-lg font-bold hover:bg-gray-300 transition">Avbryt</button>
            </div>
          </div>
        </div>
      )}

      {viewImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4" onClick={() => setViewImage(null)}>
          <div className="relative max-w-4xl w-full">
            <button onClick={() => setViewImage(null)} className="absolute top-4 right-4 bg-white text-gray-800 p-3 rounded-full hover:bg-gray-100 transition z-10">
              <X size={24} />
            </button>
            <img src={viewImage} alt="Avtale bilde" className="w-full h-auto rounded-lg" />
          </div>
        </div>
      )}

      {delConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-sm w-full">
            <div className="text-center mb-6">
              <AlertCircle className="text-orange-500 mx-auto mb-4" size={60} />
              <h2 className="text-2xl font-bold text-gray-800 mb-4">⚠️ Slette?</h2>
              <div className="bg-gray-50 rounded-2xl p-4 mb-4">
                <p className="text-xl font-bold text-gray-800 mb-2">{delConfirm.title}</p>
                <p className="text-base text-gray-600">{fmtDate(delConfirm.startTime)}</p>
                <p className="text-base text-gray-600">Kl. {fmtTime(delConfirm.startTime)}</p>
              </div>
              <p className="text-sm text-gray-600 mb-4">Dette kan ikke angres!</p>
            </div>
            <div className="space-y-3">
              <button onClick={confDel} className="w-full bg-red-500 text-white py-3 px-6 rounded-xl text-lg font-bold hover:bg-red-600 transition">🗑️ Ja, slett</button>
              <button onClick={() => setDelConfirm(null)} className="w-full bg-gray-200 text-gray-700 py-3 px-6 rounded-xl text-lg font-bold hover:bg-gray-300 transition">Avbryt</button>
            </div>
          </div>
        </div>
      )}

      {editConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-sm w-full">
            <div className="text-center mb-6">
              <Edit className="text-blue-500 mx-auto mb-4" size={60} />
              <h2 className="text-2xl font-bold text-gray-800 mb-4">✏️ Rediger?</h2>
              <div className="bg-gray-50 rounded-2xl p-4 mb-4">
                <p className="text-xl font-bold text-gray-800 mb-2">{editConfirm.title}</p>
                <p className="text-base text-gray-600">{fmtDate(editConfirm.startTime)}</p>
                <p className="text-base text-gray-600">Kl. {fmtTime(editConfirm.startTime)}</p>
              </div>
              <p className="text-sm text-gray-600 mb-4">Vil du endre denne avtalen?</p>
            </div>
            <div className="space-y-3">
              <button onClick={confirmEdit} className="w-full bg-blue-500 text-white py-3 px-6 rounded-xl text-lg font-bold hover:bg-blue-600 transition">✏️ Ja, rediger</button>
              <button onClick={() => setEditConfirm(null)} className="w-full bg-gray-200 text-gray-700 py-3 px-6 rounded-xl text-lg font-bold hover:bg-gray-300 transition">Avbryt</button>
            </div>
          </div>
        </div>
      )}

      <div className="sticky top-0 z-40">
        <div className="bg-white shadow-lg">
          <div className="max-w-5xl mx-auto p-3">
            <div className="flex items-center justify-between mb-2">
              <button onClick={() => setView('home')} className="p-2 hover:bg-gray-100 rounded-xl transition">
                <ChevronLeft size={24} />
              </button>
              <div className="text-center flex-1">
                <p className="text-sm text-gray-600">{now.toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                <p className="text-2xl font-bold text-gray-800">{now.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}</p>
              </div>
              <button onClick={() => setShowFilter(!showFilter)} className="p-2 hover:bg-gray-100 rounded-xl transition relative">
                <Filter size={24} className="text-gray-600" />
                {filter !== 'all' && <div className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full"></div>}
              </button>
            </div>

            {showFilter && (
              <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                <button onClick={() => { setFilter('all'); setShowFilter(false); }} className={`w-full px-4 py-2 rounded-lg font-bold text-sm transition ${filter === 'all' ? 'bg-blue-500 text-white shadow-lg' : 'bg-white hover:bg-gray-100'}`}>Alle</button>
                {Object.entries(cats).map(([key, c]) => (
                  <button key={key} onClick={() => { setFilter(key); setShowFilter(false); }} className={`w-full px-4 py-2 rounded-lg font-bold text-sm transition ${filter === key ? 'bg-blue-500 text-white shadow-lg' : 'bg-white hover:bg-gray-100'}`}>
                    {c.icon} {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-3 space-y-4">
        <div>
          <h2 className="text-lg font-bold text-gray-800 mb-3 bg-white rounded-xl p-3 shadow-md">📌 I dag</h2>
          <div className="space-y-3">
            {filtered(events.filter(e => sameDay(e.startTime, now))).length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
                <p className="text-gray-500 text-base font-semibold">✓ Ingen avtaler i dag</p>
              </div>
            ) : (
              filtered(events.filter(e => sameDay(e.startTime, now))).sort((a, b) => new Date(a.startTime) - new Date(b.startTime)).map(e => renderEventCard(e, true))
            )}
          </div>
        </div>

        <div>
          <h2 className="text-lg font-bold text-gray-800 mb-3 bg-white rounded-xl p-3 shadow-md">📆 Kommende</h2>
          <div className="space-y-3">
            {filtered(events.filter(e => new Date(e.startTime) > now && !sameDay(e.startTime, now))).length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
                <p className="text-gray-500 text-base font-semibold">✓ Ingen kommende avtaler</p>
              </div>
            ) : (
              filtered(events.filter(e => new Date(e.startTime) > now && !sameDay(e.startTime, now))).sort((a, b) => new Date(a.startTime) - new Date(b.startTime)).slice(0, 10).map(e => renderEventCard(e, false))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
